/**
 * Logstream interface.
 * Works kind of like a Backbone.Collection, with bonus!
 *
 * Usage:
 *
 *     // Get the static/global logstream collection object
 *     ls = require('@/collections/v5/logstream');
 *
 *     // Open a connection
 *     ls.connect();
 *
 *     // Start listening to a device's logs
 *     ls.add(device);
 *
 *     // Stop listening to a device's logs
 *     ls.remove(device);
 *
 *     // Listen for log events
 *     ls.on('log', function(data) { do something with data });
 *
 *     // Listen for a specific device
 *     ls.on('device_id:<id>' function(data) { do something with data });
 *
 *     // Listen for a specific log type
 *     ls.on('log_type:<id>' function(data) { do something with data });
 *
 * In other words, this collection is a collection of all the
 * devices you are listening to. The event stream object is abstracted
 * away somewhat. Don't overwrite logstream.es
 *
 * All log events come in as JSON, which we convert to an Object.
 * Contents of the data passed back from the EventSource
 * have the following keys:
 *  {
 *      "device_id": <device_id>,
 *      "ts": <ISO timestamp>,
 *      "log_type": <{ development, um don't know }>,
 *      "type": <{ agent.log, don't know }>,
 *      "msg": <log message>
 *  }
 *
 */
import _ from 'underscore';
import Radio from 'backbone.radio';
import v5_Collection from '@/collections/v5/collection';
import v5_Model from '@/models/v5/model';
import Api from '@/lib/api/api';
import _event_source_polyfill from 'event-source-polyfill';
    var ls = new v5_Collection();

    // ls.on('reset', function(){ console.trace(); });
    /**
     * Throttle timeout in milliseconds. Requests to log multiple devices 
     * will be separated by this much time in order to not trigger 429 errors
     * from querying the API too frequently.
     */
    var LISTEN_THROTTLE_MS = 200;

    /**
     * A list of devices we will attempt to listen to as soon as the
     * connection is active
     */
    ls.connection_queue = [];
    /**
     * Return the URL used to retrieve the key we need for
     * the event stream
     */
    function url() { return Api.urls.logstream(); }

    /**
     * Return the url for the EventStream itself
     * @param id String, the uuid that we got from POSTing to the logstream endpoint
     */
    function es_url(id) {
        return ls.url() + '/' + id + '?format=json';
    }

    /**
     * Get the logging url for a specific device
     */
    function device_stream_url(device) {
        return ls.es.url.split('?')[0] + '/' + device.id;
    }

    /**
     * Loop through the queue of devices waiting to listen for logs
     * and start listening
     */
    function connect_waiting_devices() {
        // Start logging all devices in the queue;
        // throttle so we don't run into 429s
        ls.connection_queue.forEach(function(device) {
            start_listening(device);
        });
        ls.connection_queue = [];
    }

    /**
     * Listen for EventStream messages and trigger Backbone Events
     * from the logstream collection. External modules interested in the
     * logs themselves should be listening to the events triggered in this 
     * function.
     */
    function onmessage(e) {
        var log = JSON.parse(e.data);
        ls.trigger('log', log);
        ls.trigger('device_id:' + log.device_id, log);
        ls.trigger('log_type:' + log.log_type, log);
    }

    /**
     * Listen for 'state_change' event and change aspects of the logstream
     * based on them. Namely, when we get a close event, we need to clean 
     * up the EventSource object and reset the state of the logstream. 
     *
     * When we get an open event, allow device log requests to go through and
     * request any queued device logs which may have been requested before 
     * we got the opened state.
     */
    function on_state_change(e) {
        var event = JSON.parse(e.data);
        ls.trigger('state', event.state, event);
        if (event.device_id) {
            ls.trigger('device_id:' + event.device_id, event);
        }
        switch (event.state) {
        case 'closed':
            ls.reset();
            ls.es = null;
            break;
        case 'opened':
            connect_waiting_devices();
            break;
        }
    }

    /**
     * Boolean result tells us whether we are allowed to
     * start or stop listening to individual devices,
     * ie. PUT /logstream/:logstream_id/:device_id
     */
    function allow_device_requests() {
        return !(
            ls.es === null
        ||  ls.es.readyState === EventSource.CLOSED
        );
    }
    /**
     * Create a new Eventsource based on data we got
     * from the logstream endpoint.
     *
     * Set up an event listener that triggers Backbone Events
     *
     */
    function create_es(data) {
        if (ls.es) {
            ls.es = null;
        }
        if (data.access_token) {
            handle_reconnect();
            return;
        }
        if ( !data || !data.data ) {
            close_eventstream();
            return;
        }
        ls.es = new EventSource(es_url(data.data.id) );
        ls.es.addEventListener('error', recover_from_error);

        // Capture and handle events such as "open" or "close"
        ls.es.addEventListener('state_change', on_state_change);

        // Capture and handle general messages
        ls.es.onmessage = onmessage;
    }

    function recover_from_error(e) {
        close_eventstream();
        setTimeout(function() {
            if (!navigator.onLine) return;
            connect()
            .then(function() {
                _.forEach(ls.models, start_listening);
            },
            function(err){
                console.warn('Could not connect event stream', err);
            });
        }, 1000);
    }
    function close_eventstream() {
        if (!ls.es) return;
        ls.es.close();
        ls.es = null;
    }

    /**
     * Get the Eventstream URL and create a new connection
     */
    function connect() {
        start_network_listeners();
        return Api.ajax_v5({
            url: ls.url(),
            method: 'POST'
        }).done(create_es);
    }

    /**
     * Close the event stream manually
     */
    function disconnect() {
        close_eventstream();
        return ls.reset();
    }

    /**
     * Tell the API to start sending logs for a specific device
     */
    var start_listening = _.throttle(function start_listening(device) {
        return Api.ajax_v5({
            url: device_stream_url(device),
            method: 'PUT',
            data: { id: device.id, type: device.type }
        });
    }, LISTEN_THROTTLE_MS);
    /**
     * Tell the API to stop sending logs for a specific device
     */
    function stop_listening(device) {
        return Api.ajax({
            url: device_stream_url(device),
            method: 'DELETE'
        });
    }

    /**
     * Disconnect from the logstream if the collection is empty
     *
     * It's separated out into a function here so we can call it
     * in a promise chain (and possibly to add other logic. WIP)
     */
    function disconnect_if_empty() {
        if (ls.length < 1) {
            ls.disconnect();
        }
    }
    /**
     * Handle additions to the logstream collection.
     * Either start listening immediately or enqueue the device for listening 
     * once the collection is open.
     */
    function on_add(device) {
        // Only listen to one device at a time
        //   Doing this in a conventional loop because 
        //   the ls.each syntax seemed to be causing some kind of
        //   race condition combined with ls.remove(device)
        for (var i = 0; i < ls.models.length; i++) {
            if (ls.models[i].id !== device.id)
                ls.remove(ls.models[i]);
        }
        // Start listening to the device immediately,
        // or defer until the logstream has been connected
        if (allow_device_requests()) {
            start_listening(device);
        } else {
            ls.connection_queue.push(device);
            ls.connect();
        }
    }
    /**
     * Handle device removal from the collection.
     *
     * When a device is removed, call the API DELETE for that 
     * specific device's logstream.
     *
     * If the device is the last in the collection, disconnect from 
     * the logstream entirely.
     */
    function on_remove(device) {
        if (!allow_device_requests()) return;
        stop_listening(device)
            .done(disconnect_if_empty)
        ;
    }
    function handle_disconnect() {
        close_eventstream();
        Radio.request('alerts', 'showAlert', {
            message: "Lost internet connection. Live logs will no longer display.",
            messageType: 'danger',
            timeout: 5000
        });
        window.addEventListener('online', handle_reconnect);
    }
    function handle_reconnect(){
        window.removeEventListener('online', handle_reconnect);

        ls.connect().then(function(){
            if (ls.models.length > 0) {
                _.forEach(ls.models, start_listening);
            }
        });

        Radio.request('alerts', 'showAlert', {
            message: "Regained internet connection.",
            messageType: 'success',
            timeout: 5000
        });
    }
    function start_network_listeners() {
        window.removeEventListener('offline', handle_disconnect);
        window.addEventListener('offline', handle_disconnect);
    }
    /**
     * When a device is added to the collection, tell the EventStream
     * to send us logs for that device
     */
    ls.on('add', on_add);
    /**
     * When a device is removed from the collection, tell the EventStream
     * to stop sending us logs for that device
     */
    ls.on('remove', on_remove);

    /**
     * Public interface
     */
    _.extend(ls, {
        url: url,
        model: v5_Model,
        es: null,
        connect: connect,
        disconnect: disconnect
    });

    export default ls;

